/**
 * EntityPageContent.jsx
 * Main content for entity pages (communes/EPCI)
 * Migration from front/src/components/EntityPageContent.js
 * Phase 4: Business components
 */
import React from 'react';
import { Box } from '@mui/material';

import EntityContentTitle from './EntityContentTitle.jsx';
import EntityCharacteristics from './EntityCharacteristics.jsx';
import ReadingGuide from './ReadingGuide.jsx';
import IndicatorGroup from './IndicatorGroup.jsx';
import useNavigationState from '../hooks/ui/useNavigationState.js';

/**
 * Main component for entity page content
 * Displays different sections based on content hierarchy
 *
 * @param {Object} props - Component props
 * @param {Object} props.entity - Entity data
 * @param {Array} props.contentHierarchy - Content hierarchy to display
 * @param {string} props.screenType - Screen type (mobile/desktop)
 * @returns {JSX.Element} Entity page content
 */
const EntityPageContent = ({
  entity = {},
  contentHierarchy,
  screenType,
}) => {
  const { itemsStates } = useNavigationState();

  return contentHierarchy.map(({ contentTitle: title, key, items = [] }) => {
    const { nom, code, type } = entity;

  // Characteristics section
  if (key === 'caracteristiques') {
    return (
      <Box key={key} sx={{ pageBreakInside: 'avoid' }}>
        <EntityContentTitle
          id={key}
          title={title}
          type={type}
          sx={{ pageBreakBefore: 'unset' }}
        />
        <EntityCharacteristics entity={entity} />
      </Box>
    );
  }

  // Understanding section (Reading guide)
  if (key === 'comprendre') {
    return (
      <Box key={key} sx={{ pageBreakInside: 'unset' }}>
        <EntityContentTitle
          id={key}
          title={title}
          type={type}
          sx={{ pageBreakBefore: 'unset' }}
        />
        <ReadingGuide entityType={type} code={code} name={nom} />
      </Box>
    );
  }

  // Climate indicator sections
  return (
    <IndicatorGroup
      key={key}
      entity={entity}
      itemsState={itemsStates}
      screenType={screenType}
      groupTitle={title}
      items={items}
      anchor={key}
    />
  );
});
};

EntityPageContent.displayName = 'EntityPageContent';

export default EntityPageContent;
